import { useEffect, useState } from "react";
import OwnerLayout from "../../../layout/OwnerLayout";
import { useParams } from "react-router-dom";
import { getEntertainmentVideoById } from "../../../../api/entertainmentVideo.api";
import { baseURL } from "../../../../api/api";
import { Link } from "react-router-dom";
import {
  FiEdit2,
  FiCalendar,
  FiMapPin,
  FiFilm,
  FiTag,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiMonitor,
} from "react-icons/fi";
import { format } from "date-fns";

function EntertainmentVideoDetails() {
  const { id } = useParams();
  const [videoDetails, setVideoDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getEntertainmentVideoById(id)
      .then((res) => {
        setVideoDetails(res?.data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const formatDate = (dateString) => {
    return format(new Date(dateString), "MMM dd, yyyy");
  };

  const getStatusIcon = () => {
    switch (videoDetails?.status) {
      case "approved":
        return <FiCheckCircle className="text-green-500" />;
      case "rejected":
        return <FiXCircle className="text-red-500" />;
      default:
        return <FiClock className="text-yellow-500" />;
    }
  };

  const getStatusText = () => {
    switch (videoDetails?.status) {
      case "approved":
        return "Approved";
      case "rejected":
        return "Rejected";
      default:
        return "Pending Approval";
    }
  };

  if (loading) {
    return (
      <OwnerLayout title="Loading...">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
        </div>
      </OwnerLayout>
    );
  }

  if (!videoDetails) {
    return (
      <OwnerLayout title="Video Not Found">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900">Video not found</h3>
          <p className="mt-2 text-gray-600">
            The requested video could not be loaded.
          </p>
          <Link
            to="/infotainmentvideos"
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
          >
            Back to Videos
          </Link>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout title="Video Details">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with action button */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {videoDetails.name}
            </h1>
            <div className="flex items-center mt-1">
              <span className="text-gray-500 mr-2">
                {videoDetails.category} • {videoDetails.location}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                {getStatusIcon()}
                <span className="ml-1">{getStatusText()}</span>
              </span>
            </div>
          </div>
          <Link
            to={`/infotainmentvideos/edit/${id}`}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-black bg-yellow hover:bg-yellow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow transition-colors"
          >
            <FiEdit2 className="mr-2" />
            Edit Video
          </Link>
        </div>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Video player section */}
          <div className="lg:col-span-2">
            <div className="bg-black rounded-xl overflow-hidden shadow-lg">
              <video
                src={`${baseURL}/s3/images/${videoDetails?.video?.name}`}
                className="w-full aspect-video"
                controls
                controlsList="nodownload"
              />
            </div>

            {/* Video details card */}
            <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Details
              </h2>

              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-gray-400">
                    <FiFilm />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Name</p>
                    <p className="text-sm text-gray-900">{videoDetails.name}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-gray-400">
                    <FiTag />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">
                      Category
                    </p>
                    <p className="text-sm text-gray-900">
                      {videoDetails.category}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-gray-400">
                    <FiMapPin />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">
                      Location
                    </p>
                    <p className="text-sm text-gray-900">
                      {videoDetails.location || "Not specified"}
                    </p>
                  </div>
                </div>

                {/* Status */}
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-gray-400">
                    {getStatusIcon()}
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Status</p>
                    <p className="text-sm text-gray-900">{getStatusText()}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Schedule card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Schedule
              </h2>

              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-gray-400">
                    <FiCalendar />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">
                      Start Date
                    </p>
                    <p className="text-sm text-gray-900">
                      {formatDate(videoDetails.startDate)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-gray-400">
                    <FiCalendar />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">
                      End Date
                    </p>
                    <p className="text-sm text-gray-900">
                      {formatDate(videoDetails.endDate)}
                    </p>
                  </div>
                </div>

                {/* Screens section */}
                {videoDetails.screens && videoDetails.screens.length > 0 && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-5 w-5 text-gray-400">
                        <FiMonitor />
                      </div>
                      <div className="ml-3 w-full">
                        <p className="text-sm font-medium text-gray-500">
                          Screens ({videoDetails.screens.length})
                        </p>
                        <div className="mt-2 space-y-2">
                          {videoDetails.screens.map((screen) => (
                            <Link
                              key={screen._id}
                              to={`/screendetails/${screen._id}`}
                              className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {screen.model ||
                                      `Screen ${screen.screenType}`}
                                  </p>
                                  <div className="mt-1 flex flex-wrap gap-2">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize bg-blue-100 text-blue-800">
                                      {screen.screenType === "restaurant"
                                        ? "Fixed Location"
                                        : screen.screenType === "vehicle"
                                        ? "Mobile Location"
                                        : screen.screenType}
                                    </span>
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${
                                        screen.onlineStatus === "online"
                                          ? "bg-green-100 text-green-800"
                                          : "bg-red-100 text-red-800"
                                      }`}
                                    >
                                      {screen.onlineStatus}
                                    </span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize bg-gray-100 text-gray-800">
                                      {screen.status}
                                    </span>
                                  </div>
                                </div>
                                <span className="text-xs text-gray-500">
                                  {screen.lastConnection
                                    ? `Last seen: ${format(
                                        new Date(screen.lastConnection),
                                        "MMM d, h:mm a"
                                      )}`
                                    : "Never connected"}
                                </span>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    This video will be active during the specified date range.
                  </p>
                </div>
              </div>
            </div>

            {/* Additional actions card */}
            <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Actions
              </h2>
              <div className="space-y-3">
                <Link
                  to={`/infotainmentvideos/edit/${id}`}
                  className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-yellow hover:bg-yellow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  <FiEdit2 className="mr-2" />
                  Edit Details
                </Link>
                <Link
                  to="/infotainmentvideos"
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Back to All Videos
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </OwnerLayout>
  );
}

export default EntertainmentVideoDetails;
